import { enums } from 'google-ads-api'
import { Platform } from '..'

export type NgramLookbackWindow =
    | 'LAST_30_DAYS'
    | 'LAST_90_DAYS'
    | 'LAST_180_DAYS'
    | 'LAST_365_DAYS'

export type SearchTermCampaign = Omit<
    NgramCampaign,
    'campaignGroupName' | 'campaignGroupIsBranding' | 'ga4EngagedSessions' | 'ga4Sessions'
>

export type GadsAllowedCampaignTypes =
    | enums.AdvertisingChannelType.SEARCH
    | enums.AdvertisingChannelType.SHOPPING
    | enums.AdvertisingChannelType.PERFORMANCE_MAX

export interface NgramCampaign {
    campaignId: string
    campaignName: string
    campaignGroupName: string
    campaignGroupIsBranding: boolean
    campaignType: campaignTypeEnum
    impressions: number
    clicks: number
    cost: number
    conversions: number
    conversionValue: number
    ga4Sessions: number
    ga4EngagedSessions: number
}

export interface NgramAdGroupNames {
    campaignId: string
    adGroupId: number
    adGroupName: string
}

export interface NgramCampaignNames {
    campaignId: string
    campaignName: string
}

export interface NgramData {
    ngram: string
    campaignType: campaignTypeEnum
    impressions: number
    clicks: number
    cost: number
    conversions: number
    conversionValue: number
    ga4Sessions: number
    ga4EngagedSessions: number
    hasPoorEngagement: boolean
    hasPoorIndustryPerformance: boolean
    hasLowest5PctCTR: boolean
    industryPerformanceRatio: number
    diff: number | null
    rawNscore: number | null
    searchTerms: string[]
    nscore?: number | null
    potentialSavings: number
    conversionLoss: number
    conversionCountLoss: number
    percentOfCampaignVolume: number
    negativeKeywordsDestinations?: NegativeKeywordsDestinations
    dateAdded?: string
}
export interface NgramDataWrapped {
    ngramRows: NgramData[]
    averagePerCampaignType: AveragePerCampaignType
    insightsContext: {
        campaignAvgCtr: number
    }
}

export interface SearchTermWithNGram {
    ngram: string
    lookbackWindow: NgramLookbackWindow
    campaignId: string
    campaignResourceName: string
    searchTerm: string // Only available for campaign search term insight
    searchTermResourceName: string
    clicks: number
    conversions: number
    conversionValue: number
    ga4Sessions: number
    ga4EngagedSessions: number
    campaignType: campaignTypeEnum
    adGroupResourceName?: string // Only available for search term view
    searchTermMatchType?: enums.SearchTermMatchType // Only available for search term view
    impressions?: number // Only available for campaign search term insight
    cost?: number // Only available for search term view
}

export interface AveragePerCampaignType {
    [campaignTypeWindowKey: string]: {
        performanceMetric: number // CPA, ROAS
        cpa: number // To determine whether an ngram is pending for shopping and search campaigns, we use the CPA even when in ROAS mode.
    }
}

export interface NgramFilters {
    excludeOneWordNGrams: boolean
    excludeTwoWordNGrams: boolean
    excludeThreeWordNGrams: boolean
    excludeStopwords: boolean
    excludeSearchPartners: boolean
    excludeSearchTermsBlockedByNegatives: boolean
    excludePausedKeywords: boolean
    excludeExactMatchs: boolean
    excludeExactMatchCloseVariants: boolean
    excludePhraseMatchs: boolean
    excludePhraseMatchCloseVariants: boolean
    excludeBroadMatchs: boolean
    showOnlyNgramsWithInsights: boolean
}

export interface KeywordMetrics {
    resourceName: string
    campaignName: string
    adGroupName: string
    adGroupId: number
    keywordText: string
    keywordId: number
    clicks: number
    impressions: number
    cost: number
    conversions: number
    conversionValue: number
}

export interface NegativeKeywordsDestinations {
    adGroups: {
        adGroupId: number
        adGroupName: string
        campaignName: string
    }[]
    campaigns: {
        campaignId: string
        campaignName: string
    }[]
    sharedSets: {
        sharedSetResourceName: string
        sharedSetName: string
    }[]
    accountLevel: boolean
}

export interface NegativeKeywordAttributes {
    keyword: string
    matchType: enums.KeywordMatchType
    potentialSavings: number
}

export interface NgramToolHistory {
    date: Date
    keyword: string
    matchType: enums.KeywordMatchType
    negativeKeywordsDestinations: NegativeKeywordsDestinations
    userName: string
    profileUrl: string | null
}

export interface SearchTermMetricsRow {
    searchTerm: string
    impressions: number
    clicks: number
    cost: number
    conversions: number
    conversionValue: number
    uniqueKey: string
    keywords?: string[] // when channel is 'search'
    campaignName?: string // when channel is 'pmax'
    adGroupName?: string // when channel is 'shopping'
}

export type CampaignChannelType = 'search' | 'pmax' | 'shopping'

export enum campaignTypeEnum {
    SEARCH,
    SHOPPING,
    PERFORMANCE_MAX,
}

export enum MsAllowedCampaignTypes {
    Shopping = 'Shopping',
    Search = 'Search & content',
    PerformanceMax = 'Performance max',
}

export const campaignTypeMap = {
    [Platform.Platform.GoogleAds]: {
        ['SEARCH']: campaignTypeEnum.SEARCH,
        ['SHOPPING']: campaignTypeEnum.SHOPPING,
        ['PERFORMANCE_MAX']: campaignTypeEnum.PERFORMANCE_MAX,
    },
    [Platform.Platform.MicrosoftAds]: {
        ['SEARCH']: campaignTypeEnum.SEARCH,
        ['SHOPPING']: campaignTypeEnum.SHOPPING,
        ['PERFORMANCE_MAX']: campaignTypeEnum.PERFORMANCE_MAX,
    },
} as const
